import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Stack } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ControlProps } from 'react-select';
import { components as SelectComponents } from 'react-select';

import { APIKeyServiceNames } from '@feathr/blackbox';
import type { IRadioOption } from '@feathr/components';
import {
  Alert,
  Button,
  ButtonValid,
  DatePicker,
  Input,
  Modal,
  Radios,
  Select,
} from '@feathr/components';

import { useCreateAPIKey } from './CreateAPIKeyModal.hooks';
import type { IExpirationOption } from './CreateAPIKeyModal.utils';
import {
  EExpirationPreset,
  getExpirationOptions,
  getExpiresAt,
  getExpiresAtForDate,
} from './CreateAPIKeyModal.utils';
import ScopePicker from './ScopePicker';

import * as styles from './CreateAPIKeyModal.css';

/** Expiration Select control with a leading calendar icon (Figma 2715-2949). */
function ExpirationControl({
  children,
  ...props
}: Readonly<ControlProps<IExpirationOption>>): JSX.Element {
  return (
    <SelectComponents.Control {...props}>
      <FontAwesomeIcon
        className={styles.calendarIcon}
        data-name={'expiration-calendar-icon'}
        icon={faCalendar}
      />
      {children}
    </SelectComponents.Control>
  );
}

/** Preset selected (and its expiry sent) when the user doesn't touch the control. */
const DEFAULT_EXPIRATION_PRESET = EExpirationPreset.ThirtyDays;

export interface ICreateAPIKeyModalProps {
  opened: boolean;
  onClose: () => void;
  onServiceKeyCreated: (secret: string) => void;
}

function CreateAPIKeyModal({
  onClose,
  onServiceKeyCreated,
  opened,
}: Readonly<ICreateAPIKeyModalProps>): JSX.Element {
  const { t } = useTranslation();
  const reference = useMemo(() => new Date(), []);
  const {
    createValidationErrors,
    handleCreate,
    isCreating,
    isLegacy,
    name,
    nameValidationErrors,
    selectedScopes,
    serviceName,
    setExpiresAt,
    setIsExpirationComplete,
    setName,
    setScopes,
    setServiceName,
    reset,
  } = useCreateAPIKey({
    initialExpiresAt: getExpiresAt(DEFAULT_EXPIRATION_PRESET, reference),
    onLegacyKeyCreated: onClose,
    onServiceKeyCreated,
    reference,
  });

  const [expirationPreset, setExpirationPreset] =
    useState<EExpirationPreset>(DEFAULT_EXPIRATION_PRESET);
  const [customDate, setCustomDate] = useState<Date | undefined>(undefined);

  // The modal stays mounted, so start each open from a clean form rather than
  // the previous session's service, name, scopes, and expiration.
  useEffect(() => {
    if (opened) {
      reset();
      setExpirationPreset(DEFAULT_EXPIRATION_PRESET);
      setCustomDate(undefined);
    }
  }, [opened, reset]);

  const expirationOptions = useMemo(() => getExpirationOptions(t, reference), [reference, t]);

  const serviceOptions: IRadioOption[] = [
    { id: APIKeyServiceNames.FeathrAPI, name: t('Feathr API') },
    { id: APIKeyServiceNames.Zapier, name: t('Zapier') },
  ];

  function handleChangeName(value?: string): void {
    setName(value ?? '');
  }

  function handleChangeService(value?: string): void {
    setServiceName(value as APIKeyServiceNames);
  }

  const selectedExpirationOption = expirationOptions.find(
    (option) => option.id === expirationPreset,
  );

  function handleChangeExpiration(option: IExpirationOption): void {
    setExpirationPreset(option.id);

    if (option.id === EExpirationPreset.Custom) {
      setExpiresAt(customDate ? getExpiresAtForDate(customDate) : undefined);
      setIsExpirationComplete(customDate !== undefined);
      return;
    }

    setCustomDate(undefined);
    setExpiresAt(getExpiresAt(option.id, reference));
    setIsExpirationComplete(true);
  }

  function handleChangeCustomDate(dateStr?: string): void {
    const date = dateStr ? new Date(dateStr) : undefined;
    setCustomDate(date);
    setExpiresAt(date ? getExpiresAtForDate(date) : undefined);
    setIsExpirationComplete(date !== undefined);
  }

  async function handleClickCreate(): Promise<void> {
    await handleCreate();
  }

  const rightActions = (
    <>
      <Button disabled={isCreating} onClick={onClose} type={'naked'}>
        {t('Cancel')}
      </Button>
      <ButtonValid
        errors={createValidationErrors}
        isLoading={isCreating}
        name={'create-api-key'}
        onClick={handleClickCreate}
        type={'primary'}
      >
        {t('Create')}
      </ButtonValid>
    </>
  );

  return (
    <Modal
      bodyClassName={styles.modalBody}
      contentClassName={styles.modalContent}
      onClose={onClose}
      opened={opened}
      rightActions={rightActions}
      title={t('Create API key')}
    >
      <Stack className={styles.root} gap={'md'}>
        <Radios
          label={t('Service')}
          layout={'block'}
          name={'api-key-service'}
          onChange={handleChangeService}
          options={serviceOptions}
          value={serviceName}
        />
        {/* No name field for Zapier: the API stores every legacy key as
            "Zapier", so asking the user to name one only to discard it made the
            list show a name they never chose, and made a second key fail with
            "Key already exists for this service" naming a service they never
            typed. V1 picked the service from a fixed list for the same reason. */}
        {isLegacy ? (
          <Alert
            title={t(
              'This key is named "Zapier". Your account can have one active Zapier key at a time.',
            )}
            type={'info'}
          />
        ) : (
          <Input
            helpText={t('A label to help you recognize this key later.')}
            label={t('Key name')}
            name={'api-key-name'}
            onChange={handleChangeName}
            placeholder={t('e.g. my-secret-key')}
            type={'text'}
            validationError={nameValidationErrors}
            value={name}
          />
        )}
        {/* Legacy (Zapier) keys are scopeless with a fixed-life JWT — no expiration or scopes. */}
        {!isLegacy && (
          <>
            <Select<IExpirationOption>
              components={{ Control: ExpirationControl }}
              helpPlacement={'bottom'}
              helpText={
                expirationPreset === EExpirationPreset.NoExpiration
                  ? undefined
                  : t('The key will expire on the selected date')
              }
              label={t('Expiration')}
              name={'api-key-expiration'}
              onSelectSingle={handleChangeExpiration}
              options={expirationOptions}
              value={selectedExpirationOption}
            />
            {expirationPreset === EExpirationPreset.Custom && (
              <DatePicker
                label={t('Expiration date')}
                minDate={reference}
                name={'api-key-custom-expiration'}
                onClear={handleChangeCustomDate}
                onDateStrChange={handleChangeCustomDate}
                placeholder={t('Select a date')}
                selected={customDate}
                showCalendarIcon={true}
              />
            )}
            {expirationPreset === EExpirationPreset.NoExpiration && (
              <Alert
                title={t('This key remains valid indefinitely. Store it securely.')}
                type={'warning'}
              />
            )}
            <ScopePicker onChange={setScopes} selected={selectedScopes} />
          </>
        )}
      </Stack>
    </Modal>
  );
}

export default observer(CreateAPIKeyModal);
